/* eslint-disable @typescript-eslint/strict-boolean-expressions */
// @ts-nocheck
import {
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Controls } from 'components/shared/Controls';
import PnLChart from 'components/optionsCalculator/PnLChart';
import PnlHeatmap from 'components/optionsCalculator/pnlChart/PnlHeatmap';
import BaseDateSettings from 'components/optionsCalculator/pnlChart/BaseDateSettings';
import {
  HEATMAP_METRICS,
  HeatmapMetric,
} from 'components/optionsCalculator/pnlChart/heatmapMetrics';
import PositionContainer from 'components/optionsCalculator/positionManagement/PositionContainer';
import TickerOverview from 'components/optionsCalculator/TickerOverview';
import SectionShell from 'components/optionsCalculator/layout/SectionShell';
import { usePersistedState } from 'hooks/usePersistedState';
import { ChartSizingButton } from 'components/shared/ChartSizingButton';
import { COMBINED_SIGNALS, PRODUCT_LINKS } from 'config';
import { useOptionsCalculatorActions } from 'hooks/optionsCalculator/useOptionsCalculatorActions';
import { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  searchSuggestionsData,
  searchHandlerState,
  hiroSymbolsState,
  hasInternalDashAccessState,
} from 'states';
import { EquityFieldKey, GREEK_IDX, OPTION_IDX, ProductType } from 'types';
import { SearchHandlerFunction, SuggestionSearchData } from 'types/shared';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { useOptionsCalculatorData } from 'hooks/optionsCalculator/useOptionsCalculatorData';
import {
  OptionType,
  NEW_POSITION_ID,
  OC_SAVED_POSITIONS_CACHE_KEY,
  Position as PositionType,
  OptionsCalculatorDraftState,
  OCSettingsItem,
  OC_DEFAULT_SYM,
  StrikeDeltaObject,
  PositionLeg,
} from 'types/optionsCalculator';
import {
  buildLegExpiryCorrections,
  generateDefaultOptionLeg,
  getUpdatedLeg,
  getStringifiedPosition,
} from 'util/optionsCalculator';
import { useQuery } from '@tanstack/react-query';
import usePrices from 'hooks/equityhub/usePrices';
import useImpliedVolatility from 'hooks/iVol/useImpliedVolatility';
import StrategiesContainer from 'components/optionsCalculator/Strategies/StrategiesContainer';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ShareIcon from '@mui/icons-material/Share';
import ZoomOut from '@mui/icons-material/ZoomOut';
import { optionsCalculatorShowKeyLevelsState } from 'states/optionsCalculator';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import { useOptionsCalculatorComputed } from 'hooks/optionsCalculator/useOptionsCalculatorComputed';
import { DownloadChartButton } from 'components/shared/DownloadChartButton';
import { SGTooltip } from 'components/core';
import { InfoButton } from 'components/shared';
import { getVolFromTableData } from 'util/riskEngine';
import { useDividends } from 'hooks/useDividends';
import { transformDividendsToRiskEngineFormat } from 'util/dividendUtils';

const isValidSymbol = (sym: string) => {
  return !COMBINED_SIGNALS.has(sym);
};

interface OCSectionCollapsedState {
  pnlChart: boolean;
  heatmap: boolean;
  positions: boolean;
}

const OC_SECTION_COLLAPSED_STORAGE_KEY =
  'options_calculator_collapsed_sections';

const DEFAULT_OC_SECTION_COLLAPSED_STATE: OCSectionCollapsedState = {
  pnlChart: false,
  heatmap: false,
  positions: false,
};

const SECTION_HEIGHT_PERCENTAGE = {
  header: 15,
  pnlChart: 30,
  heatmap: 30,
  positions: 25,
};

const sectionHeightPercentage = (
  section: keyof SECTION_HEIGHT_PERCENTAGE,
  collapsedSections: OCSectionCollapsedState,
): number => {
  const total = Object.entries(SECTION_HEIGHT_PERCENTAGE)
    .filter(([k, _v]) => collapsedSections[k] !== true)
    .reduce((accum, [_key, value]) => accum + value, 0);
  return Math.round((SECTION_HEIGHT_PERCENTAGE[section] / total) * 100);
};

interface OptionsCalculatorContainerProps {
  /**
   * Wires this instance into the app's global top-nav symbol search bar —
   * the standalone page's only way to change ticker. A canvas widget has
   * its own symbol selector instead, and multiple canvas instances would
   * otherwise fight over this shared singleton, so canvas passes `false`.
   *
   * Also doubles as the "is this the standalone page" flag for the Share
   * button: the standalone page keeps its position in the URL already, so
   * it shares the current page URL as-is; a canvas widget keeps its
   * position on the component's own config instead, so it shares a link to
   * the standalone page with the position encoded onto the URL.
   */
  enableGlobalSymbolSearch?: boolean;

  /** Hide the ticker/company name header and the TickerOverview + StrategiesContainer row. */
  hideHeader?: boolean;
  /** Hide the P/L Chart section. */
  hidePnlChart?: boolean;
  /** Hide the Heatmaps section. Always hidden for non-internal users. */
  hideHeatmap?: boolean;
  /** Hide the Positions section. */
  hidePositions?: boolean;

  /** whether the positions widget is greatly simplified for inclusion in other components. */
  simplifedPositions?: boolean;

  /** Height (px) of the P/L Chart section, outside fullscreen. Defaults to 500. */
  chartHeight?: number;

  /** Space reserved below the Positions section - a compact embed stacking
   * multiple instances should reduce this to avoid compounding gaps. */
  positionsBottomMargin?: string | number;

  /** Space between sections, in theme spacing units - a compact embed can tighten it. */
  sectionGap?: number;

  /** Whether the Positions section starts collapsed - opts out of the normal
   * app-wide persisted preference and uses this local default instead. */
  defaultPositionsCollapsed?: boolean;
}

const OptionsCalculatorContainer = ({
  enableGlobalSymbolSearch = true,
  hideHeader = false,
  hidePnlChart = false,
  hideHeatmap: hideHeatmapProp = false,
  hidePositions = false,
  simplifedPositions = false,
  chartHeight,
  positionsBottomMargin = '72px',
  sectionGap = 4,
  defaultPositionsCollapsed,
}: OptionsCalculatorContainerProps = {}) => {
  const [tab, setTab] = useState<'new' | 'saved'>('new');
  const hiroSyms = useRecoilValue(hiroSymbolsState);
  const isInternalUser = useRecoilValue(hasInternalDashAccessState);
  // Heatmaps are internal-only for now, regardless of what the caller passed.
  // TODO(ross) 2026.09.11 - remove isInternalUser flag when ready to release.
  const hideHeatmap = hideHeatmapProp || !isInternalUser;
  const [chartFullscreen, setChartFullscreen] = useState<boolean>(false);
  const [showKeyLevels, setShowKeyLevels] = useRecoilState(
    optionsCalculatorShowKeyLevelsState,
  );

  const [forceDefaultZoom, setForceDefaultZoom] = useState<boolean>(true);
  const [showLegend, setShowLegend] = useState<boolean>(true);
  const [copied, setCopied] = useState<boolean>(false);

  const [collapsedSections, setCollapsedSections] =
    useState<OCSectionCollapsedState>(DEFAULT_OC_SECTION_COLLAPSED_STATE);
  usePersistedState<OCSectionCollapsedState>(
    OC_SECTION_COLLAPSED_STORAGE_KEY,
    collapsedSections,
    (saved) => {
      if (saved != null && typeof saved === 'object') {
        setCollapsedSections((prev) => ({ ...prev, ...saved }));
      }
    },
    [],
  );
  const toggleSectionCollapsed = (key: keyof OCSectionCollapsedState) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // When set, Positions opts out of the shared/persisted collapsedSections
  // entirely and uses this local default instead - see defaultPositionsCollapsed.
  const [localPositionsCollapsed, setLocalPositionsCollapsed] = useState(
    defaultPositionsCollapsed ?? false,
  );
  const positionsCollapsed =
    defaultPositionsCollapsed != null
      ? localPositionsCollapsed
      : collapsedSections.positions;
  const togglePositionsCollapsed =
    defaultPositionsCollapsed != null
      ? () => setLocalPositionsCollapsed((prev) => !prev)
      : () => toggleSectionCollapsed('positions');
  // Feeds sectionHeightPercentage the value Positions actually renders with,
  // so the P/L chart's height stays correct under the local override too.
  const effectiveCollapsedSections: OCSectionCollapsedState = {
    ...collapsedSections,
    positions: positionsCollapsed,
  };

  const setSuggestionsData = useSetRecoilState(searchSuggestionsData);
  const setSuggestionsHandler = useSetRecoilState(searchHandlerState);
  const { getCurrentGreeksData } = useImpliedVolatility({
    bypassAccessCheck: true,
  });
  const { getPrices } = usePrices({ bypassAccessCheck: true });
  const hasInitializedRef = useRef<boolean>(false);

  const {
    setActivePosition,
    getSavedPositions,
    updateBaseDate,
    getRiskFreeRate,
    setEnabledHeatmapMetrics,
  } = useOptionsCalculatorActions();
  const {
    sym,
    baseDate,
    legs,
    shares,
    ivSettings,
    allGreeksData,
    allPrices,
    currentKeyLevels,
    allEquities,
    allVolDataCall,
    allVolDataPut,
    currentEquity,
    isLoading: isPnlLoading,
    enabledHeatmapMetrics,
  } = useOptionsCalculatorData();
  const { pnlChartRef } = useOptionsCalculatorComputed();

  // Centralized draft state
  const [draftState, setDraftState] = useState<OptionsCalculatorDraftState>({
    newPosition: {
      id: NEW_POSITION_ID,
      option_legs: legs,
      underlying_shares: shares,
      iv_adjustments: ivSettings,
      sym: sym,
    },
    savedPositionDrafts: new Map(),
    activePositionId: NEW_POSITION_ID,
    hasUnsavedChanges: new Map(),
  });

  // Query for saved positions
  const { data: savedPositions, isFetching: isFetchingSavedPositions } =
    useQuery({
      queryKey: [OC_SAVED_POSITIONS_CACHE_KEY],
      queryFn: getSavedPositions,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      gcTime: Infinity,
    });

  const { data: dividendsData } = useDividends({ sym });
  const { dividendDates, dividendAmounts } = useMemo(
    () => transformDividendsToRiskEngineFormat(dividendsData ?? []),
    [dividendsData],
  );

  // Initialize search suggestions
  useEffect(() => {
    if (!enableGlobalSymbolSearch) {
      return;
    }
    const suggestionsHandler: SearchHandlerFunction = async (
      val: string,
    ): Promise<void> => {
      if (val != null && val != '') {
        const [newGreeks, newPricesPayload] = await Promise.all([
          getCurrentGreeksData(val),
          getPrices([val]),
        ]);

        const defaultLeg = generateDefaultOptionLeg(
          val,
          getRiskFreeRate,
          newGreeks ?? undefined,
          newPricesPayload?.get(val) ?? undefined,
        );

        if (defaultLeg) {
          // Reset to new position and overwrite completely with default leg
          const defaultNewPosition = {
            id: NEW_POSITION_ID,
            option_legs: defaultLeg ? [defaultLeg] : [],
            underlying_shares: [],
            iv_adjustments: [],
            sym: val,
          };
          updatePosition(NEW_POSITION_ID, defaultNewPosition, true);
          updateBaseDate(defaultLeg.expiry);
          setTab('new'); // Switch to new position tab
        }
      }
    };
    setSuggestionsHandler(() => suggestionsHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSuggestionsHandler, enableGlobalSymbolSearch]);

  useEffect(() => {
    if (!enableGlobalSymbolSearch) {
      return;
    }
    const result: SuggestionSearchData[] = Array.from(hiroSyms.values()).map(
      (data: string) => ({
        symbol: data,
        name: allEquities?.get(data)?.[EquityFieldKey.name] || data,
      }),
    );

    const suggestions = result.filter(
      (value: { symbol: string; name: string }) => isValidSymbol(value.symbol),
    );
    setSuggestionsData(suggestions);
  }, [setSuggestionsData, hiroSyms, allEquities, enableGlobalSymbolSearch]);

  // Initialize new position with default leg when symbol/data is available
  useEffect(() => {
    if (!hasInitializedRef.current && sym && allGreeksData && allPrices) {
      // If the page already has legs or shares, mark as initialized to prevent reset
      if (
        draftState.newPosition.option_legs.length > 0 ||
        draftState.newPosition.underlying_shares.length > 0
      ) {
        hasInitializedRef.current = true;
        return;
      }

      // Only create default leg if position is empty
      if (
        draftState.newPosition.option_legs.length === 0 &&
        draftState.newPosition.underlying_shares.length === 0
      ) {
        const symToUse = isValidSymbol(sym) ? sym : OC_DEFAULT_SYM;
        const defaultLeg = generateDefaultOptionLeg(
          symToUse,
          getRiskFreeRate,
          allGreeksData.get(symToUse),
          allPrices.get(symToUse),
        );
        if (defaultLeg) {
          updatePosition(
            NEW_POSITION_ID,
            {
              option_legs: [defaultLeg],
              underlying_shares: [],
              iv_adjustments: [],
              sym: symToUse,
            },
            true,
          );
          updateBaseDate(defaultLeg.expiry);
          hasInitializedRef.current = true;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sym,
    allGreeksData,
    allPrices,
    draftState.newPosition.option_legs.length,
    draftState.newPosition.underlying_shares.length,
  ]);

  // Initialize saved position drafts when saved positions are loaded
  // Preserve existing drafts to prevent losing unsaved changes
  useEffect(() => {
    if (savedPositions && savedPositions.length > 0) {
      setDraftState((prev) => {
        const newDrafts = new Map(prev.savedPositionDrafts);

        // Only update positions that don't already have drafts
        // This preserves existing draft changes when data refetches
        savedPositions.forEach((position) => {
          if (!newDrafts.has(position.id)) {
            newDrafts.set(position.id, { ...position });
          } else {
            // Position already has a draft - only update if it doesn't have unsaved changes
            const hasUnsavedChanges = prev.hasUnsavedChanges.get(position.id);
            if (!hasUnsavedChanges) {
              newDrafts.set(position.id, { ...position });
            }
            // If it has unsaved changes, keep the existing draft intact
          }
        });

        return {
          ...prev,
          savedPositionDrafts: newDrafts,
        };
      });
    }
  }, [savedPositions]);

  // Get the currently active position (either new or saved draft)
  const activePosition = useMemo((): PositionType => {
    if (draftState.activePositionId === NEW_POSITION_ID) {
      return draftState.newPosition;
    }

    const savedDraft = draftState.savedPositionDrafts.get(
      draftState.activePositionId,
    );
    if (savedDraft) {
      return savedDraft;
    }

    // Fallback to global state (shouldn't happen in normal flow)
    return {
      id: draftState.activePositionId,
      option_legs: legs,
      underlying_shares: shares,
      iv_adjustments: ivSettings,
      sym: sym,
    };
  }, [draftState, legs, shares, ivSettings, sym]);

  // Handler for switching active position - makes global state updates explicit
  const handleSetActivePosition = useCallback(
    (positionId: number = NEW_POSITION_ID, position?: PositionType) => {
      // Update local draft state
      setDraftState((prev) => ({
        ...prev,
        activePositionId: positionId,
      }));

      // Determine the position to activate
      let positionToActivate: PositionType;
      if (positionId === NEW_POSITION_ID) {
        positionToActivate = position ?? draftState.newPosition;
      } else {
        const updatedPosition =
          position ?? draftState.savedPositionDrafts.get(positionId);
        if (updatedPosition == null) {
          return;
        }
        positionToActivate = updatedPosition;
      }

      setActivePosition(positionToActivate);
    },
    [draftState.newPosition, draftState.savedPositionDrafts, setActivePosition],
  );

  // Unified position update function (works for both new and saved positions)
  const updatePosition = useCallback(
    (
      positionId: number,
      updates: Partial<PositionType>,
      overrideActivePosition: boolean = false,
    ) => {
      setDraftState((prev) => {
        const updatedState = { ...prev };

        // Update the appropriate position
        if (positionId === NEW_POSITION_ID) {
          updatedState.newPosition = overrideActivePosition
            ? ({ ...updates, id: NEW_POSITION_ID } as PositionType)
            : { ...prev.newPosition, ...updates, id: NEW_POSITION_ID };
        } else {
          const currentDraft = prev.savedPositionDrafts.get(positionId);
          if (currentDraft) {
            const updatedDraft = overrideActivePosition
              ? ({ ...updates } as PositionType)
              : { ...currentDraft, ...updates };
            const newDrafts = new Map(prev.savedPositionDrafts);
            newDrafts.set(positionId, updatedDraft);
            updatedState.savedPositionDrafts = newDrafts;
          }
        }

        // Mark as having unsaved changes (if it applies)
        // Check if the updated position matches the original saved position
        let hasChanges = true;
        if (positionId !== NEW_POSITION_ID) {
          const originalPosition = savedPositions?.find(
            (p) => p.id === positionId,
          );
          if (originalPosition != null) {
            const draft = updatedState.savedPositionDrafts.get(positionId);

            if (draft != null) {
              // Compare relevant fields to determine if there are actual changes
              hasChanges =
                draft.sym !== originalPosition.sym ||
                JSON.stringify(draft.option_legs) !==
                  JSON.stringify(originalPosition.option_legs) ||
                JSON.stringify(draft.underlying_shares) !==
                  JSON.stringify(originalPosition.underlying_shares) ||
                JSON.stringify(draft.iv_adjustments) !==
                  JSON.stringify(originalPosition.iv_adjustments);
            }
          }
        }

        updatedState.hasUnsavedChanges = new Map(prev.hasUnsavedChanges).set(
          positionId,
          hasChanges,
        );

        return updatedState;
      });

      // If updating the currently active position, sync with global state
      if (
        positionId === draftState.activePositionId ||
        overrideActivePosition
      ) {
        const updatedPosition = overrideActivePosition
          ? ({ ...updates } as PositionType)
          : positionId === NEW_POSITION_ID
            ? { ...draftState.newPosition, ...updates }
            : {
                ...draftState.savedPositionDrafts.get(positionId)!,
                ...updates,
              };

        handleSetActivePosition(positionId, updatedPosition);
        setForceDefaultZoom(true);
      }
    },
    [draftState, savedPositions, handleSetActivePosition, setForceDefaultZoom],
  );

  // draftState.newPosition only snapshots legs once at mount, so it misses the context-level expiry correction above.
  useEffect(() => {
    const symGreeks = allGreeksData?.get(draftState.newPosition.sym);
    if (symGreeks == null || draftState.newPosition.option_legs.length === 0) {
      return;
    }
    const corrections = buildLegExpiryCorrections(
      draftState.newPosition.option_legs,
      symGreeks,
    );
    if (corrections.length === 0) {
      return;
    }
    const correctedExpiryByLegId = new Map(
      corrections.map(({ legId, expiry }) => [legId, expiry]),
    );
    updatePosition(NEW_POSITION_ID, {
      option_legs: draftState.newPosition.option_legs.map((leg) => {
        const expiry = correctedExpiryByLegId.get(leg.leg_id);
        return expiry != null ? { ...leg, expiry } : leg;
      }),
    });
  }, [allGreeksData, draftState.newPosition, updatePosition]);

  // Reset unsaved changes flag (called after successful save)
  const markPositionAsSaved = (positionId: number) => {
    setDraftState((prev) => {
      const newHasUnsavedChanges = new Map(prev.hasUnsavedChanges);
      newHasUnsavedChanges.set(positionId, false);
      return {
        ...prev,
        hasUnsavedChanges: newHasUnsavedChanges,
      };
    });
  };

  // Handle strike bar changes from PnLChart
  const handleLegChange = (
    legId: number,
    updates: Partial<PositionLeg>,
    shouldUseDefaultPrice = false,
  ) => {
    const currentLeg = activePosition.option_legs?.find(
      (leg) => leg.leg_id === legId,
    );

    if (!currentLeg || !allGreeksData?.get(sym)) {
      return;
    }

    // Use getUpdatedLeg utility function to handle the update logic
    const updatedLeg = getUpdatedLeg(
      currentLeg,
      updates,
      shouldUseDefaultPrice,
      allGreeksData.get(sym)!,
    );

    const updatedLegs = activePosition.option_legs?.map((leg) =>
      leg.leg_id === legId ? updatedLeg : leg,
    );

    updatePosition(draftState.activePositionId, {
      option_legs: updatedLegs ?? [],
    });
  };

  // Remove a saved position draft (called after deletion)
  const removeSavedPositionDraft = (positionId: number) => {
    setDraftState((prev) => {
      const newDrafts = new Map(prev.savedPositionDrafts);
      newDrafts.delete(positionId);

      const newHasUnsavedChanges = new Map(prev.hasUnsavedChanges);
      newHasUnsavedChanges.delete(positionId);

      // If the deleted position was active, switch to new position
      const newActivePositionId =
        prev.activePositionId === positionId
          ? NEW_POSITION_ID
          : prev.activePositionId;

      return {
        ...prev,
        savedPositionDrafts: newDrafts,
        hasUnsavedChanges: newHasUnsavedChanges,
        activePositionId: newActivePositionId,
      };
    });

    // If we switched to new position due to deletion, update global state
    if (positionId === draftState.activePositionId) {
      setActivePosition(draftState.newPosition);
    }
  };

  // Add new saved position draft (called after successful save of new position)
  const addSavedPositionDraft = (position: PositionType) => {
    setDraftState((prev) => {
      const newDrafts = new Map(prev.savedPositionDrafts);
      newDrafts.set(position.id, { ...position });

      return {
        ...prev,
        savedPositionDrafts: newDrafts,
        activePositionId: position.id, // Switch to the newly saved position
        hasUnsavedChanges: new Map(prev.hasUnsavedChanges).set(
          position.id,
          false,
        ),
      };
    });

    // Update global state to reflect the newly saved and active position
    setActivePosition(position);
  };

  const strikeDataPerExpiration: Map<number, StrikeDeltaObject[]> =
    useMemo(() => {
      const greeksData = allGreeksData?.get(sym);
      if (!greeksData) {
        return new Map<number, StrikeDeltaObject[]>();
      }

      const strikesMap = new Map<number, StrikeDeltaObject[]>();

      // Get all available expirations from greeks data
      Object.keys(greeksData).forEach((expiration: string) => {
        const greeks = greeksData[Number(expiration)];
        if (greeks) {
          const strikeDeltas = Object.keys(greeks)
            .map((strike: string) => {
              const strikeNum = Number(strike);
              const callGreeks = greeks[strikeNum]?.[OPTION_IDX.CALL];
              const putGreeks = greeks[strikeNum]?.[OPTION_IDX.PUT];
              const callDelta = callGreeks?.[GREEK_IDX.DELTA];
              const putDelta = putGreeks?.[GREEK_IDX.DELTA];
              return { strike: strikeNum, callDelta, putDelta };
            })
            .sort((a, b) => a.strike - b.strike);
          strikesMap.set(Number(expiration), strikeDeltas);
        }
      });

      return strikesMap;
    }, [allGreeksData, sym]);

  const addNewLegToActivePosition = useCallback(
    (optType: OptionType) => {
      const newLeg = generateDefaultOptionLeg(
        activePosition.sym,
        getRiskFreeRate,
        allGreeksData?.get(activePosition.sym),
        allPrices?.get(activePosition.sym),
        undefined,
        undefined,
        optType,
      );

      if (newLeg == null) {
        return;
      }

      if (newLeg != null && activePosition.option_legs != null) {
        newLeg.expiry = activePosition.option_legs.reduce(
          (acc, l) => Math.min(acc, l.expiry),
          Infinity,
        );
      }

      updatePosition(activePosition.id, {
        option_legs: [
          ...(activePosition.option_legs ?? []),
          {
            ...newLeg,
            position_id: activePosition.id,
          },
        ],
      });
    },
    // TODO: updatePosition could be converted to a useCallback itself to avoid deps lint
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activePosition, allGreeksData, allPrices],
  );

  const onDeleteLeg = useCallback(
    (legId: number) => {
      const updatedLegs = activePosition.option_legs?.filter(
        (leg) => leg.leg_id !== legId,
      );
      updatePosition(activePosition.id, { option_legs: updatedLegs ?? [] });
    },
    [updatePosition, activePosition.option_legs, activePosition.id],
  );

  const legIVs = new Map(
    isPnlLoading
      ? []
      : (activePosition.option_legs
          .map((leg) => {
            const iv = getVolFromTableData(
              leg.option_type === OptionType.CALL
                ? allVolDataCall.get(sym)!
                : allVolDataPut.get(sym)!,
              leg.strike,
              leg.expiry,
            );

            return [leg.leg_id, iv] as const;
          })
          .filter(([_, iv]) => iv != null) as [number, number][]),
  );

  const hasLegs = legs.length > 0;
  const hasShares = shares.length > 0;
  const hasPosition = hasLegs || hasShares;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: sectionGap,
        height: chartFullscreen ? '100%' : 'auto',
      }}
    >
      {!hideHeader && (
        <>
          <Stack sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
            <Typography variant="h6" color="text.primary">
              {sym}
            </Typography>
            {currentEquity && (
              <Typography variant="body2" color="text.secondary">
                {currentEquity.name}
              </Typography>
            )}
          </Stack>
          <Box
            sx={{
              gap: 2,
              display: chartFullscreen ? 'none' : 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              width: '100%',
            }}
          >
            <Stack
              sx={{
                position: 'relative',
                bgcolor: 'background.paper',
                borderRadius: 2,
                p: '12px',
                maxWidth: { xs: '100%', md: '50%' },
                minWidth: { xs: '100%', md: '350px' },
              }}
            >
              <TickerOverview />
            </Stack>

            <Stack
              sx={{
                flex: 1,
                position: 'relative',
                bgcolor: 'background.paper',
                borderRadius: 2,
                p: '12px',
              }}
            >
              <StrategiesContainer
                onUpdateNewPosition={(updates: Partial<PositionType>) => {
                  updatePosition(NEW_POSITION_ID, updates, true);
                  setDraftState((prev) => ({
                    ...prev,
                    activePositionId: NEW_POSITION_ID,
                  }));
                }}
                greeksData={allGreeksData?.get(sym)}
                currentPrice={allPrices?.get(sym)}
                sym={sym}
                setTab={setTab}
                setBaseDate={updateBaseDate}
                setForceDefaultZoom={setForceDefaultZoom}
                dividendDates={dividendDates}
                dividendAmounts={dividendAmounts}
              />
            </Stack>
          </Box>
        </>
      )}
      {!hidePnlChart && (
        <SectionShell
          title="P&L Chart"
          collapsed={collapsedSections.pnlChart}
          onToggleCollapsed={() => toggleSectionCollapsed('pnlChart')}
          sx={{
            minHeight: collapsedSections.pnlChart
              ? 'auto'
              : `max(350px, min(${sectionHeightPercentage('pnlChart', effectiveCollapsedSections)}%, 550px))`,
            height: chartFullscreen ? '100%' : 'auto',
          }}
        >
          {hasPosition && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                mb: 3,
              }}
            >
              <Stack direction="row" spacing={2}>
                <SGTooltip title="Add an at-the-money call to the PnL chart, defaulting to the nearest expiration within the position">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => addNewLegToActivePosition(OptionType.CALL)}
                    sx={{ fontSize: '14px', textTransform: 'none' }}
                  >
                    Add Call
                  </Button>
                </SGTooltip>
                <SGTooltip title="Add an at-the-money put to the PnL chart, defaulting to the nearest expiration within the position">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => addNewLegToActivePosition(OptionType.PUT)}
                    sx={{ fontSize: '14px', textTransform: 'none' }}
                  >
                    Add Put
                  </Button>
                </SGTooltip>
              </Stack>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: { xs: 2, md: 3 },
                  flexWrap: 'wrap',
                }}
              >
                <SGTooltip title="Reset the PnL chart to the default view for that options, showing a clear view of the position's value across strikes">
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => setForceDefaultZoom(true)}
                    sx={{
                      textTransform: 'none',
                      fontSize: { xs: '11px', sm: '12px' },
                      fontWeight: 500,
                    }}
                    startIcon={<ZoomOut fontSize="small" />}
                    id={OCSettingsItem.RESTORE_DEFAULT_VIEW}
                  >
                    Restore Default View
                  </Button>
                </SGTooltip>

                <SGTooltip
                  title={
                    enableGlobalSymbolSearch
                      ? 'Copy the URL to share your PnL chart with others, who can paste the link to see the same chart'
                      : "Copy a link to this position on the standalone Options Calculator page, since this canvas widget's own URL doesn't carry it"
                  }
                >
                  <Button
                    onClick={() => {
                      // The standalone page already keeps the position in its
                      // own URL (state is URL-synced there), so share it
                      // as-is. A canvas widget keeps its position on the
                      // component's own config instead, so its page URL
                      // wouldn't reproduce anything — build a link to the
                      // standalone page with the position encoded onto it.
                      const shareUrl = enableGlobalSymbolSearch
                        ? window.location.href
                        : `${window.location.origin}${PRODUCT_LINKS[ProductType.OPTIONS_CALCULATOR].link}?${getStringifiedPosition(
                            {
                              sym,
                              option_legs: legs,
                              underlying_shares: shares,
                              iv_adjustments: ivSettings,
                            },
                            baseDate,
                          )}`;
                      void navigator.clipboard.writeText(shareUrl);
                      setCopied(true);
                      setTimeout(() => {
                        setCopied(false);
                      }, 3_000);
                    }}
                    size="small"
                    id="share-url"
                    startIcon={
                      copied ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <ShareIcon fontSize="small" />
                      )
                    }
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      fontSize: { xs: '11px', sm: '12px' },
                      fontWeight: 500,
                    }}
                  >
                    {copied ? (
                      <>
                        <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          Copied to clipboard!
                        </Box>
                        <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                          Copied!
                        </Box>
                      </>
                    ) : (
                      'Share'
                    )}
                  </Button>
                </SGTooltip>

                {currentKeyLevels && currentKeyLevels.length > 0 && (
                  <SGTooltip title="Overlay SpotGamma key levels for the selected ticker directly onto the PnL chart">
                    <FormControlLabel
                      color="primary"
                      control={
                        <Switch
                          size="small"
                          checked={showKeyLevels}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => setShowKeyLevels(event.target.checked)}
                          id={OCSettingsItem.KEY_LEVELS}
                        />
                      }
                      label={
                        <>
                          <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            Key Levels
                          </Box>
                          <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                            Levels
                          </Box>
                        </>
                      }
                      labelPlacement="end"
                      sx={{
                        '& .MuiFormControlLabel-label': {
                          color: 'primary.main',
                          fontSize: { xs: '11px', sm: '14px' },
                        },
                        mr: 1,
                      }}
                    />
                  </SGTooltip>
                )}

                <SGTooltip title="Show or hide the PnL chart's legend">
                  <FormControlLabel
                    color="primary"
                    control={
                      <Switch
                        size="small"
                        checked={showLegend}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => setShowLegend(event.target.checked)}
                        id={OCSettingsItem.LEGEND}
                      />
                    }
                    label="Legend"
                    labelPlacement="end"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        color: 'primary.main',
                        fontSize: { xs: '11px', sm: '14px' },
                      },
                      mr: 1,
                    }}
                  />
                </SGTooltip>

                <DownloadChartButton
                  key="pnl-download"
                  chartRef={pnlChartRef}
                  selectedSym={sym}
                  chartName="PnL"
                  description="Download a snapshot of the current PnL chart"
                  logoSrc="/images/logo.png"
                />

                <ChartSizingButton
                  onClick={() => setChartFullscreen((prev) => !prev)}
                  icon={
                    chartFullscreen ? (
                      <FullscreenExitIcon fontSize="small" color="primary" />
                    ) : (
                      <FullscreenIcon fontSize="small" color="primary" />
                    )
                  }
                  title="Expand the PnL chart to occupy the full window"
                  id={OCSettingsItem.FULLSCREEN}
                />
                <InfoButton key={`pnl-info`} articleKey="options-calculator" />
              </Box>
            </Box>
          )}
          <PnLChart
            showKeyLevels={showKeyLevels}
            showLegend={showLegend}
            chartHeight={chartHeight}
            forceDefaultZoom={forceDefaultZoom}
            setForceDefaultZoom={setForceDefaultZoom}
            isFullscreen={chartFullscreen}
            onLegChange={handleLegChange}
            onLegDelete={onDeleteLeg}
            strikeDataPerExpiration={strikeDataPerExpiration}
            legIVs={legIVs}
          />
          <BaseDateSettings
            legs={legs}
            baseDate={baseDate}
            updateBaseDate={updateBaseDate}
          />
        </SectionShell>
      )}
      {!hideHeatmap && (
        <SectionShell
          title="Heatmaps"
          collapsed={collapsedSections.heatmap}
          onToggleCollapsed={() => toggleSectionCollapsed('heatmap')}
          hidden={chartFullscreen}
          headerActions={
            <Controls
              buttonGroups={[
                {
                  buttons: HEATMAP_METRICS.map(({ value, label }) => ({
                    value,
                    label,
                  })),
                  setter: (value: HeatmapMetric) =>
                    setEnabledHeatmapMetrics([value]),
                  curValue: enabledHeatmapMetrics[0] ?? 'pnl',
                },
              ]}
            />
          }
        >
          <PnlHeatmap
            dividends={dividendsData ?? []}
            enabledMetrics={enabledHeatmapMetrics}
          />
        </SectionShell>
      )}
      {!hidePositions && (
        <SectionShell
          title="Positions"
          collapsed={positionsCollapsed}
          onToggleCollapsed={togglePositionsCollapsed}
          hidden={chartFullscreen}
          sx={{ mb: positionsBottomMargin }}
        >
          <PositionContainer
            tab={tab}
            setTab={setTab}
            greeksLoading={isPnlLoading}
            // Pass down all draft state and handlers
            activePositionId={draftState.activePositionId}
            onSetActivePositionId={handleSetActivePosition}
            // New position draft state
            newPosition={draftState.newPosition}
            onUpdateNewPosition={(updates: Partial<PositionType>) =>
              updatePosition(NEW_POSITION_ID, updates)
            }
            // Saved positions draft state
            savedPositionDrafts={draftState.savedPositionDrafts}
            onUpdateSavedPositionDraft={(
              positionId: number,
              updates: Partial<PositionType>,
            ) => updatePosition(positionId, updates)}
            onRemoveSavedPositionDraft={removeSavedPositionDraft}
            onAddSavedPositionDraft={addSavedPositionDraft}
            // Change tracking
            hasUnsavedChanges={draftState.hasUnsavedChanges}
            onMarkPositionAsSaved={markPositionAsSaved}
            // Saved positions data
            savedPositions={savedPositions ?? []}
            isFetchingSavedPositions={isFetchingSavedPositions}
            setForceDefaultZoom={setForceDefaultZoom}
            strikeDataPerExpiration={strikeDataPerExpiration}
            legIVs={legIVs}
            simplifedPositions={simplifedPositions}
          />
        </SectionShell>
      )}
    </Box>
  );
};

export default OptionsCalculatorContainer;
